<template>
  <div class="create-group-wrapper">
    <!-- Header Mobile-Friendly -->
    <div class="mobile-header">
      <v-app-bar color="#00479b" dark flat>
        <v-btn icon @click="$router.go(-1)" class="mr-2">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <v-toolbar-title class="text-h6 font-weight-medium">
          Create Njangi Group
        </v-toolbar-title>
      </v-app-bar>
    </div>

    <!-- Progress Stepper - Mobile Optimized -->
    <div class="progress-indicator pa-3">
      <v-stepper v-model="currentStep" class="elevation-0">
        <v-stepper-header class="elevation-0">
          <v-stepper-step
            :complete="currentStep > 1"
            step="1"
            class="step-mobile"
            color="#00479b"
          >
            <div class="step-content">
              <v-icon small class="mr-1">mdi-shield-check</v-icon>
              <span class="step-text">Terms</span>
            </div>
          </v-stepper-step>

          <v-divider></v-divider>

          <v-stepper-step
            :complete="currentStep > 2"
            step="2"
            class="step-mobile"
            color="#00479b"
          >
            <div class="step-content">
              <v-icon small class="mr-1">mdi-information</v-icon>
              <span class="step-text">Details</span>
            </div>
          </v-stepper-step>

          <v-divider></v-divider>

          <v-stepper-step
            step="3"
            class="step-mobile"
            color="#00479b"
          >
            <div class="step-content">
              <v-icon small class="mr-1">mdi-account-plus</v-icon>
              <span class="step-text">Invite</span>
            </div>
          </v-stepper-step>
        </v-stepper-header>
      </v-stepper>
    </div>

    <!-- Main Content -->
    <v-container class="content-container pa-0">
      <!-- Step 1: Terms -->
      <div v-if="currentStep === 1" class="step-content-wrapper">
        <div class="content-card">
          <div class="card-header pa-4" style="background: linear-gradient(135deg, #00479b 0%, #2c6bb5 100%);">
            <h3 class="white--text mb-0">
              <v-icon left color="white" class="mb-1">mdi-shield-account-outline</v-icon>
              Group Creator Responsibilities
            </h3>
          </div>

          <v-card-text class="pa-4">
            <!-- Terms Summary -->
            <div class="terms-summary mb-4">
              <div class="term-item d-flex align-center mb-3">
                <v-icon color="#00479b" class="mr-3">mdi-check-circle</v-icon>
                <span>You are fully responsible for all group activities</span>
              </div>
              <div class="term-item d-flex align-center mb-3">
                <v-icon color="#00479b" class="mr-3">mdi-check-circle</v-icon>
                <span>You must verify all members' identities and reliability</span>
              </div>
              <div class="term-item d-flex align-center mb-3">
                <v-icon color="#00479b" class="mr-3">mdi-check-circle</v-icon>
                <span>PettyCash is only a platform, not responsible for defaults</span>
              </div>
            </div>

            <!-- Expandable Terms -->
            <v-expansion-panels flat class="mb-4">
              <v-expansion-panel>
                <v-expansion-panel-header class="expansion-header">
                  <div class="d-flex align-center">
                    <v-icon color="#00479b" class="mr-2">mdi-account-tie</v-icon>
                    <span class="font-weight-medium">Leadership & Administration</span>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content class="pt-3">
                  <p>You manage all group activities, maintain financial transparency, and resolve conflicts.</p>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <v-expansion-panel>
                <v-expansion-panel-header class="expansion-header">
                  <div class="d-flex align-center">
                    <v-icon color="#00479b" class="mr-2">mdi-account-search</v-icon>
                    <span class="font-weight-medium">Member Verification</span>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content class="pt-3">
                  <p>You must verify all members' identities and assess their reliability.</p>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <v-expansion-panel>
                <v-expansion-panel-header class="expansion-header">
                  <div class="d-flex align-center">
                    <v-icon color="#00479b" class="mr-2">mdi-currency-usd</v-icon>
                    <span class="font-weight-medium">Financial Responsibility</span>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content class="pt-3">
                  <p>You bear all financial risks from member defaults or losses.</p>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <!-- Warning Box -->
            <div class="warning-card pa-3 mb-4" style="background-color: #fff8e1; border-left: 4px solid #ff9800;">
              <div class="d-flex align-start">
                <v-icon color="#ff9800" class="mr-2 mt-1">mdi-alert</v-icon>
                <div>
                  <p class="mb-1" style="color: #333; font-size: 0.875rem;">
                    <strong>PettyCash is only a platform provider.</strong> We assume NO responsibility for group management, member defaults, or financial losses.
                  </p>
                </div>
              </div>
            </div>

            <!-- Accept Checkbox -->
            <v-checkbox
              v-model="acceptCreatorTerms"
              hide-details
              class="mt-4"
            >
              <template v-slot:label>
                <div style="font-size: 0.875rem; line-height: 1.4;">
                  <span>I accept all responsibilities as group creator and understand PettyCash's limited role.</span>
                </div>
              </template>
            </v-checkbox>

            <!-- Continue Button -->
            <div class="action-buttons mt-6">
              <v-btn
                block
                large
                color="#00479b"
                dark
                @click="currentStep = 2"
                :disabled="!acceptCreatorTerms"
                class="continue-btn"
                elevation="0"
              >
                <v-icon left>mdi-check</v-icon>
                Continue to Group Details
              </v-btn>
            </div>
          </v-card-text>
        </div>
      </div>

      <!-- Step 2: Group Details -->
      <div v-if="currentStep === 2" class="step-content-wrapper">
        <div class="content-card">
          <div class="card-header pa-4" style="background: linear-gradient(135deg, #00479b 0%, #2c6bb5 100%);">
            <h3 class="white--text mb-0">
              <v-icon left color="white" class="mb-1">mdi-pencil-outline</v-icon>
              Group Information
            </h3>
          </div>

          <v-form ref="groupForm" class="pa-4">
            <!-- Group Name -->
            <div class="form-group mb-4">
              <label class="form-label mb-2">Group Name *</label>
              <v-text-field
                v-model="groupForm.name"
                placeholder="Enter group name"
                outlined
                dense
                hide-details
                class="custom-input"
              ></v-text-field>
            </div>

            <!-- Description -->
            <div class="form-group mb-4">
              <label class="form-label mb-2">Description (Optional)</label>
              <v-textarea
                v-model="groupForm.information"
                placeholder="Describe your group's purpose"
                outlined
                rows="2"
                hide-details
                class="custom-input"
              ></v-textarea>
            </div>

            <!-- Rules -->
            <div class="form-group mb-4">
              <label class="form-label mb-2">Group Rules (Optional)</label>
              <v-textarea
                v-model="groupForm.rules"
                placeholder="Set clear rules for members"
                outlined
                rows="2"
                hide-details
                class="custom-input"
              ></v-textarea>
            </div>

            <!-- Collection Cycle (must be selected first) -->
            <div class="form-group mb-4">
              <label class="form-label mb-2">Collection Cycle *</label>
              <v-select
                v-model="groupForm.collecting_cycle"
                :items="cycleOptions"
                outlined
                dense
                hide-details
                class="custom-input"
                @change="loadContributionAmounts"
              ></v-select>
            </div>

            <!-- Contribution Amount with fees -->
            <div class="form-group mb-4">
              <label class="form-label mb-2">Contribution Amount *</label>
              <v-select
                v-model="groupForm.contribution_amount"
                :items="contributionOptions"
                item-text="display_text"
                item-value="value"
                outlined
                dense
                :loading="loadingAmounts"
                :disabled="loadingAmounts || !groupForm.collecting_cycle"
                hide-details
                class="custom-input"
                @change="updateFeeDisplay"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="#00479b">mdi-currency-usd</v-icon>
                </template>
              </v-select>

              <!-- Fee Breakdown Display -->
              <div v-if="selectedContribution && selectedContribution.fee > 0" class="fee-breakdown mt-2 pa-3" style="background-color: #e3f2fd; border-radius: 8px;">
                <div class="d-flex justify-space-between align-center mb-1">
                  <span class="caption" style="color: #00479b;">Base Contribution:</span>
                  <span class="caption font-weight-medium" style="color: #00479b;">{{ formatAmount(selectedContribution.value) }} F CFA</span>
                </div>
                <div class="d-flex justify-space-between align-center mb-1">
                  <span class="caption" style="color: #ff9800;">Transaction Fee:</span>
                  <span class="caption font-weight-medium" style="color: #ff9800;">+ {{ formatAmount(selectedContribution.fee) }} F CFA</span>
                </div>
                <v-divider class="my-2"></v-divider>
                <div class="d-flex justify-space-between align-center">
                  <span class="body-2 font-weight-bold" style="color: #00479b;">Total to Pay:</span>
                  <span class="body-2 font-weight-bold" style="color: #00479b;">{{ formatAmount(selectedContribution.total) }} F CFA</span>
                </div>
              </div>

              <div v-if="!groupForm.collecting_cycle" class="caption mt-1" style="color: #ff5722;">
                Please select a collection cycle first
              </div>
            </div>

            <!-- Start Date Selection -->
            <div class="form-group mb-4">
              <label class="form-label mb-2">Start Date *</label>
              <v-text-field
                v-model="groupForm.startDate"
                type="date"
                outlined
                dense
                hide-details
                class="custom-input"
                :min="minStartDate"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="#00479b">mdi-calendar</v-icon>
                </template>
              </v-text-field>
              <div class="caption mt-1" style="color: #666;">
                Select when your group should start collecting contributions
              </div>
            </div>

            <!-- Image Upload -->
            <div class="form-group mb-6">
              <label class="form-label mb-2">Group Image (Optional)</label>
              <div
                @click="$refs.njangi_image.click()"
                class="image-upload-area pa-4 text-center"
                :class="{ 'has-image': njangi_image }"
              >
                <input
                  type="file"
                  ref="njangi_image"
                  @change="handleFileUpload"
                  accept="image/*"
                  style="display: none"
                />
                <v-icon size="40" :color="njangi_image ? '#00479b' : '#999'">
                  {{ njangi_image ? 'mdi-image-check' : 'mdi-image-plus' }}
                </v-icon>
                <p class="mt-2 mb-0" :style="{ color: njangi_image ? '#00479b' : '#666' }">
                  {{ njangi_image ? njangi_image.name : 'Tap to upload image' }}
                </p>
                <small v-if="njangi_image" style="color: #666;">
                  {{ (njangi_image.size / 1024).toFixed(1) }} KB
                </small>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="action-buttons mt-6">
              <v-btn
                block
                large
                color="#00479b"
                dark
                @click="validateAndCreateGroup"
                :loading="creatingGroup"
                :disabled="creatingGroup"
                class="create-btn"
                elevation="0"
              >
                <v-icon left>mdi-check-circle</v-icon>
                {{ creatingGroup ? 'Creating...' : 'Create Group' }}
              </v-btn>

              <v-btn
                text
                block
                class="mt-3 back-btn"
                @click="currentStep = 1"
              >
                <v-icon left>mdi-arrow-left</v-icon>
                Back
              </v-btn>
            </div>
          </v-form>
        </div>
      </div>

      <!-- Step 3: Invite Members -->
      <div v-if="currentStep === 3" class="step-content-wrapper">
        <div class="content-card">
          <!-- Success Message -->
          <div class="success-card pa-4 mb-4 text-center" style="background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%); border-radius: 12px;">
            <v-icon color="#4caf50" size="60" class="mb-3">mdi-check-circle</v-icon>
            <h3 class="mb-2" style="color: #2e7d32;">Group Created Successfully!</h3>
            <p class="mb-0" style="color: #388e3c;">
              "{{ groupForm.name }}" is ready. Invite members to start.
            </p>
          </div>

          <!-- Share Options -->
          <div class="share-section mb-6">
            <h4 class="section-title mb-3">
              <v-icon left color="#00479b">mdi-share-variant</v-icon>
              Share Group
            </h4>

            <div class="share-options">
              <!-- Link Copy -->
              <div class="share-option mb-3">
                <v-text-field
                  :value="inviteLink"
                  readonly
                  outlined
                  dense
                  hide-details
                  class="share-input"
                >
                  <template v-slot:prepend-inner>
                    <v-icon color="#00479b">mdi-link</v-icon>
                  </template>
                  <template v-slot:append>
                    <v-btn
                      small
                      icon
                      @click="copyInviteLink"
                      color="#00479b"
                    >
                      <v-icon>mdi-content-copy</v-icon>
                    </v-btn>
                  </template>
                </v-text-field>
                <div class="caption mt-1 text-center" style="color: #666;">
                  Tap to copy invitation link
                </div>
              </div>

              <!-- WhatsApp Share -->
              <div class="share-option">
                <v-btn
                  block
                  large
                  color="#25D366"
                  dark
                  @click="shareViaWhatsApp"
                  class="whatsapp-btn"
                  elevation="0"
                >
                  <v-icon left>mdi-whatsapp</v-icon>
                  Share via WhatsApp
                </v-btn>
              </div>
            </div>
          </div>

          <!-- Invite Methods -->
          <div class="invite-methods">
            <h4 class="section-title mb-3">
              <v-icon left color="#00479b">mdi-account-plus</v-icon>
              Invite Members
            </h4>

            <v-tabs v-model="inviteTab" center-active class="invite-tabs">
              <v-tab class="invite-tab">
                <v-icon left small>mdi-account-search</v-icon>
                Search Users
              </v-tab>
              <v-tab class="invite-tab">
                <v-icon left small>mdi-phone</v-icon>
                By Phone
              </v-tab>
            </v-tabs>

            <v-tabs-items v-model="inviteTab" class="invite-tab-content">
              <!-- Tab 1: Search Users -->
              <v-tab-item>
                <div class="tab-content pa-3">
                  <v-text-field
                    v-model="searchUsername"
                    placeholder="Search by username or email"
                    outlined
                    dense
                    @input="searchUsers"
                    clearable
                    hide-details
                    class="mb-4"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon color="#666">mdi-magnify</v-icon>
                    </template>
                  </v-text-field>

                  <!-- Search Results -->
                  <div v-if="searchResults.length > 0" class="search-results">
                    <div v-for="user in searchResults" :key="user.id" class="user-card pa-3 mb-2" @click="addUserToInviteList(user)">
                      <div class="d-flex align-center">
                        <v-avatar size="40" color="#f0f0f0" class="mr-3">
                          <v-icon v-if="!user.profile_picture" color="#666">mdi-account</v-icon>
                          <v-img v-else :src="user.profile_picture"></v-img>
                        </v-avatar>
                        <div class="flex-grow-1">
                          <div class="user-name font-weight-medium">{{ user.username }}</div>
                          <div v-if="user.phone_number" class="user-phone caption" style="color: #666;">
                            {{ user.phone_number }}
                          </div>
                        </div>
                        <v-icon color="#00479b">mdi-plus-circle</v-icon>
                      </div>
                    </div>
                  </div>

                  <!-- No Results -->
                  <div v-else-if="searchUsername && searchUsername.length >= 2" class="no-results pa-4 text-center">
                    <v-icon color="#999" size="40" class="mb-2">mdi-account-question</v-icon>
                    <p class="mb-0" style="color: #666;">No users found</p>
                  </div>

                  <!-- Selected Users -->
                  <div v-if="invitedUsers.length > 0" class="selected-users mt-4">
                    <div class="d-flex justify-space-between align-center mb-2">
                      <span class="font-weight-medium">Selected ({{ invitedUsers.length }})</span>
                      <v-btn
                        small
                        color="#00479b"
                        dark
                        @click="addMembersToGroup"
                        :loading="addingMembers"
                        :disabled="addingMembers || invitedUsers.length === 0"
                      >
                        <v-icon left small>mdi-send</v-icon>
                        Invite All
                      </v-btn>
                    </div>

                    <div class="selected-chips">
                      <v-chip
                        v-for="user in invitedUsers"
                        :key="user.id"
                        close
                        @click:close="removeUserFromInviteList(user)"
                        class="ma-1"
                        color="#e3f2fd"
                        text-color="#00479b"
                      >
                        <v-avatar left size="24">
                          <v-icon small color="#00479b">mdi-account</v-icon>
                        </v-avatar>
                        {{ user.username }}
                      </v-chip>
                    </div>
                  </div>
                </div>
              </v-tab-item>

              <!-- Tab 2: Phone Invite -->
              <v-tab-item>
                <div class="tab-content pa-3">
                  <v-text-field
                    v-model="phoneInvite"
                    placeholder="+237 6XX XX XX XX"
                    outlined
                    dense
                    hide-details
                    class="mb-4"
                    type="tel"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon color="#666">mdi-phone</v-icon>
                    </template>
                  </v-text-field>

                  <v-btn
                    block
                    large
                    color="#00479b"
                    dark
                    :disabled="!phoneInvite"
                    @click="inviteByPhone"
                    elevation="0"
                    class="mb-3"
                  >
                    <v-icon left>mdi-send</v-icon>
                    Send SMS Invitation
                  </v-btn>

                  <p class="caption text-center mb-0" style="color: #666;">
                    We'll send an SMS with the invitation link
                  </p>
                </div>
              </v-tab-item>
            </v-tabs-items>
          </div>

          <!-- View Group Button -->
          <div class="action-buttons mt-6">
            <v-btn
              block
              large
              color="#00479b"
              dark
              @click="goToGroupDetail"
              class="view-group-btn"
              elevation="0"
            >
              <v-icon left>mdi-eye</v-icon>
              View My Group
            </v-btn>
          </div>
        </div>
      </div>
    </v-container>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  data() {
    return {
      currentStep: 1,
      inviteTab: 0,
      creatingGroup: false,
      loadingAmounts: false,
      addingMembers: false,

      acceptCreatorTerms: false,

      groupForm: {
        name: '',
        information: '',
        rules: '',
        contribution_amount: null,
        collecting_cycle: 'Weekly',
        startDate: ''
      },

      contributionOptions: [],
      selectedContribution: null,

      njangi_image: null,
      searchUsername: '',
      searchResults: [],
      invitedUsers: [],
      phoneInvite: '',
      inviteLink: '',
      whatsappLink: '',
      groupId: null,

      cycleOptions: [
        { text: 'Daily', value: 'Daily' },
        { text: 'Weekly', value: 'Weekly' },
        { text: 'Monthly', value: 'Monthly' }
      ]
    };
  },

  computed: {
    minStartDate() {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  },

  mounted() {
    this.loadContributionAmounts();

    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    this.groupForm.startDate = `${year}-${month}-${day}`;
  },

  methods: {
    async loadContributionAmounts() {
      if (!this.groupForm.collecting_cycle) return;

      this.loadingAmounts = true;

      try {
        const response = await axios.get('/api/v1/njangi/contribution-amounts/', {
          params: { cycle: this.groupForm.collecting_cycle }
        });

        this.contributionOptions = response.data.contributions;

        this.groupForm.contribution_amount = null;
        this.selectedContribution = null;

      } catch (error) {
        console.error('Error loading contribution amounts:', error);
        this.showSnackbar('Error loading contribution amounts', 'error');
      } finally {
        this.loadingAmounts = false;
      }
    },

    updateFeeDisplay() {
      if (this.groupForm.contribution_amount) {
        this.selectedContribution = this.contributionOptions.find(
          opt => opt.value === this.groupForm.contribution_amount
        );
      } else {
        this.selectedContribution = null;
      }
    },

    formatAmount(amount) {
      return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    },

    handleFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.njangi_image = file;
      }
    },

    async validateAndCreateGroup() {
      if (!this.groupForm.name || !this.groupForm.contribution_amount ||
          !this.groupForm.collecting_cycle || !this.groupForm.startDate) {
        this.showSnackbar('Please fill in all required fields', 'error');
        return;
      }

      this.creatingGroup = true;

      try {
        let formData = new FormData();
        formData.append('accept_terms', 'true');
        formData.append('name', this.groupForm.name);
        formData.append('contribution_amount', this.groupForm.contribution_amount);
        formData.append('collecting_cycle', this.groupForm.collecting_cycle);
        formData.append('start_date', this.groupForm.startDate);

        if (this.groupForm.information && this.groupForm.information.trim()) {
          formData.append('information', this.groupForm.information);
        }

        if (this.groupForm.rules && this.groupForm.rules.trim()) {
          formData.append('rules', this.groupForm.rules);
        }

        if (this.njangi_image) {
          formData.append('image', this.njangi_image);
        }

        const response = await axios.post('/api/v1/njangi/groups/create/', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        this.groupId = response.data.group_id;
        this.inviteLink = response.data.invite_link;
        this.whatsappLink = response.data.whatsapp_link;

        this.currentStep = 3;
        this.showSnackbar('Group created successfully!', 'success');

      } catch (error) {
        const errorMsg = error.response?.data?.error
          || error.response?.data?.message
          || error.message
          || 'Error creating group';
        this.showSnackbar(errorMsg, 'error');
      } finally {
        this.creatingGroup = false;
      }
    },

    copyInviteLink() {
      if (this.inviteLink) {
        navigator.clipboard.writeText(this.inviteLink);
        this.showSnackbar('Link copied to clipboard', 'success');
      }
    },

    shareViaWhatsApp() {
      if (this.whatsappLink) {
        window.open(this.whatsappLink, '_blank');
      } else {
        this.showSnackbar('Unable to share at this time', 'error');
      }
    },

    goToGroupDetail() {
      if (this.groupId) {
        this.$router.push(`/njangi/groups/detail/${this.groupId}`);
      }
    },

    async searchUsers() {
      if (this.searchUsername.length < 2) {
        this.searchResults = [];
        return;
      }

      try {
        const response = await axios.get('/api/v1/njangi/search-users/', {
          params: { q: this.searchUsername }
        });
        this.searchResults = response.data;
      } catch (error) {
        console.error('Search error:', error);
      }
    },

    addUserToInviteList(user) {
      if (!this.invitedUsers.find(u => u.id === user.id)) {
        this.invitedUsers.push(user);
        this.showSnackbar('Added to invite list', 'success');
      }
    },

    removeUserFromInviteList(user) {
      this.invitedUsers = this.invitedUsers.filter(u => u.id !== user.id);
    },

    async addMembersToGroup() {
      if (this.invitedUsers.length === 0) {
        this.showSnackbar('No users selected', 'warning');
        return;
      }

      this.addingMembers = true;

      try {
        const response = await axios.post(
          `/api/v1/njangi/groups/${this.groupId}/add-members/`,
          { invited_users: this.invitedUsers.map(u => u.id) }
        );

        this.showSnackbar('Invitations sent successfully', 'success');
        this.invitedUsers = [];
        this.searchResults = [];
        this.searchUsername = '';

      } catch (error) {
        const errorMsg = error.response?.data?.error || 'Error sending invitations';
        this.showSnackbar(errorMsg, 'error');
      } finally {
        this.addingMembers = false;
      }
    },

    async inviteByPhone() {
      if (!this.phoneInvite || !this.inviteLink) {
        this.showSnackbar('Please provide a phone number', 'error');
        return;
      }

      try {
        const response = await axios.post('/api/v1/njangi/send-sms-invite/', {
          phone_number: this.phoneInvite,
          invite_link: this.inviteLink,
          group_name: this.groupForm.name
        });

        this.showSnackbar('SMS invitation sent', 'success');
        this.phoneInvite = '';
      } catch (error) {
        const errorMsg = error.response?.data?.error || 'Error sending SMS';
        this.showSnackbar(errorMsg, 'error');
      }
    },

    showSnackbar(message, color) {
      this.$store.commit('setSnackBarMessage', message);
      this.$store.commit('setSnackBarColor', color === 'success' ? '#00479b' : '#d32f2f');
      this.$store.commit('activateSnackBar', true);
    }
  }
};
</script>

<style scoped>
.create-group-wrapper {
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%);
  min-height: 100vh;
}

.mobile-header {
  position: sticky;
  top: 0;
  z-index: 100;
}

.progress-indicator {
  background: white;
  border-bottom: 1px solid #e0e0e0;
}

.step-mobile {
  flex: 1;
  min-width: 0;
}

.step-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.step-text {
  font-size: 0.75rem;
  margin-top: 4px;
}

.content-container {
  max-width: 800px;
  margin: 0 auto;
}

.step-content-wrapper {
  padding: 16px;
}

.content-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 71, 155, 0.1);
}

.card-header {
  color: white;
}

.terms-summary .term-item {
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.terms-summary .term-item:last-child {
  border-bottom: none;
}

.expansion-header {
  background-color: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 4px;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-weight: 500;
  color: #333;
  font-size: 0.875rem;
  margin-bottom: 6px;
}

.custom-input >>> .v-input__control {
  min-height: 44px;
}

.custom-input >>> .v-text-field__slot input {
  font-size: 0.9375rem;
}

.fee-breakdown {
  font-size: 0.875rem;
  line-height: 1.6;
}

.image-upload-area {
  border: 2px dashed #ddd;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s;
  background-color: #fafafa;
}

.image-upload-area.has-image {
  border-color: #00479b;
  background-color: #f0f7ff;
}

.image-upload-area:hover {
  border-color: #00479b;
  background-color: #f8fbff;
}

.action-buttons {
  padding: 0 16px 24px;
}

.continue-btn,
.create-btn,
.view-group-btn {
  border-radius: 10px;
  height: 52px;
  font-weight: 500;
  text-transform: none;
  letter-spacing: normal;
}

.back-btn {
  color: #666;
  text-transform: none;
  letter-spacing: normal;
}

.success-card {
  border-radius: 16px;
}

.section-title {
  color: #333;
  font-size: 1.125rem;
  font-weight: 500;
}

.share-input >>> .v-input__control {
  border-radius: 10px;
}

.whatsapp-btn {
  border-radius: 10px;
  height: 52px;
  text-transform: none;
  letter-spacing: normal;
}

.invite-tabs >>> .v-tabs-bar {
  background-color: transparent;
  height: 48px;
}

.invite-tab {
  text-transform: none;
  letter-spacing: normal;
  font-weight: 500;
  color: #666;
  min-width: 0;
  flex: 1;
}

.invite-tab.v-tab--active {
  color: #00479b;
}

.invite-tab-content {
  background-color: transparent;
}

.tab-content {
  background-color: #f8f9fa;
  border-radius: 12px;
  margin-top: 8px;
}

.user-card {
  background: white;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid #e0e0e0;
}

.user-card:hover {
  border-color: #00479b;
  box-shadow: 0 2px 8px rgba(0, 71, 155, 0.1);
}

.no-results {
  background-color: #f8f9fa;
  border-radius: 10px;
}

.selected-chips {
  max-height: 120px;
  overflow-y: auto;
}

.selected-chips >>> .v-chip {
  border-radius: 20px;
}

@media (max-width: 600px) {
  .step-content-wrapper {
    padding: 12px;
  }

  .content-card {
    border-radius: 12px;
  }

  .continue-btn,
  .create-btn,
  .view-group-btn,
  .whatsapp-btn {
    height: 48px;
    font-size: 0.9375rem;
  }

  .invite-tab {
    font-size: 0.875rem;
  }

  .user-card {
    padding: 12px;
  }
}

@media (min-width: 601px) {
  .create-group-wrapper {
    background: #f5f7fa;
  }

  .content-card {
    margin: 20px auto;
    max-width: 600px;
  }
}

.selected-chips::-webkit-scrollbar {
  width: 6px;
}

.selected-chips::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.selected-chips::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.selected-chips::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>